/**
 * S15 — Govern · Incident Register (`/govern/incidents`).
 *
 * Track, triage and resolve AI incidents — with a narrative timeline,
 * cross-links to the agent + policy that raised it, and post-mortem
 * closure flows. RBAC-gated via `manage:incident`.
 */
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  AlertOctagon,
  CircleCheck,
  Clock3,
  RefreshCw,
  X,
  ExternalLink,
  ShieldAlert,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntityList } from "@/lib/useEntity";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { can, readOnlyReason } from "@/lib/rbac";
import { cn } from "@/lib/utils";
import { sevDot, severityRank } from "@/lib/severityStyles";
import { Flywheel } from "@/components/brand/Flywheel";
import { KpiTile } from "@/pages/dashboard/components/DashboardPrimitives";
import { toast } from "sonner";

const STATUS_TABS = [
  { id: "all",    label: "All" },
  { id: "open",   label: "Open" },
  { id: "triage", label: "Triage" },
  { id: "closed", label: "Closed" },
];

const SEV_OPTIONS = [
  { value: "",     label: "Any severity" },
  { value: "high", label: "High" },
  { value: "med",  label: "Medium" },
  { value: "low",  label: "Low" },
];

const CATEGORY_OPTIONS = [
  { value: "",          label: "Any category" },
  { value: "Privacy",   label: "Privacy" },
  { value: "Quality",   label: "Quality" },
  { value: "Vendor",    label: "Vendor" },
  { value: "Financial", label: "Financial" },
  { value: "Audit",     label: "Audit" },
];

const SORTS = [
  { id: "severity-desc", label: "Severity · high → low",   cmp: (a, b) => severityRank(b.severity) - severityRank(a.severity) },
  { id: "days-desc",     label: "Open · longest first",     cmp: (a, b) => (b.days || 0) - (a.days || 0) },
  { id: "reported-desc", label: "Reported · newest first",  cmp: (a, b) => (b.reported || "").localeCompare(a.reported || "") },
];

const STATUS_TONE = {
  open:   "bg-risk-high/10 text-risk-high",
  triage: "bg-gold-300/15  text-gold-700",
  closed: "bg-risk-low/10  text-risk-low",
};
function statusTone(status) { return STATUS_TONE[status] || STATUS_TONE.closed; }

export default function IncidentRegister() {
  const { user } = useAuth();
  const { items: incidents, loading, error, lastSyncedAt, refetch } = useEntityList("/api/incidents");
  const { isCustomer } = useTenantData();
  const navigate = useNavigate();

  const [status, setStatus] = useState("all");
  const [severity, setSeverity] = useState("");
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [sortId, setSortId] = useState("severity-desc");
  const [selected, setSelected] = useState(null);
  const [overrides, setOverrides] = useState({}); // id -> { status, closedAt }

  const writeAllowed = can(user?.id, "manage:incident");

  const items = useMemo(
    () => incidents.map((i) => ({ ...i, ...(overrides[i.id] || {}) })),
    [incidents, overrides],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cmp = SORTS.find((s) => s.id === sortId)?.cmp || SORTS[0].cmp;
    return items
      .filter((i) => {
        if (status !== "all" && i.status !== status) return false;
        if (severity && i.severity !== severity) return false;
        if (category && i.category !== category) return false;
        if (q && !`${i.id} ${i.title} ${i.agentName || ""} ${i.owner} ${i.category || ""}`.toLowerCase().includes(q)) return false;
        return true;
      })
      .slice()
      .sort(cmp);
  }, [items, status, severity, category, search, sortId]);

  const counts = useMemo(() => ({
    open:    items.filter((i) => i.status === "open").length,
    triage:  items.filter((i) => i.status === "triage").length,
    high:    items.filter((i) => i.severity === "high" && i.status !== "closed").length,
    closedWk: items.filter((i) => i.status === "closed").length,
  }), [items]);

  const resetFilters = useCallback(() => {
    setStatus("all"); setSeverity(""); setCategory(""); setSearch(""); setSortId("severity-desc");
  }, []);

  const closeIncident = useCallback((inc) => {
    if (!writeAllowed) { toast.error(readOnlyReason(user?.id)); return; }
    setOverrides((o) => ({ ...o, [inc.id]: { status: "closed", closedAt: new Date().toUTCString() } }));
    toast.success(`Closed ${inc.id} — ${inc.title}`, { description: "Closure recorded in the audit chain." });
    setSelected((s) => (s && s.id === inc.id ? { ...s, status: "closed" } : s));
  }, [writeAllowed, user?.id]);

  if (loading && incidents.length === 0) return <IncidentSkeleton />;
  if (error) return <IncidentError message={error.message} />;
  if (isCustomer && incidents.length === 0) {
    return <CustomerEmpty userName={user?.name?.split(" ")[0]} onSetup={() => navigate("/govern/agents")} />;
  }

  return (
    <div className="relative px-6 lg:px-8 py-6 anim-fade-up" data-testid="incident-register">
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.05} />
      </div>

      <Header lastSyncedAt={lastSyncedAt} syncing={loading} onRefetch={refetch} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <KpiTile label="Open incidents"   value={counts.open.toString()}     delta={`${counts.triage} in triage`}      deltaTone={counts.open > 0 ? "down" : "up"}    hint="awaiting action"                icon={AlertOctagon} tone="risk"     testId="inc-kpi-open" />
        <KpiTile label="In triage"        value={counts.triage.toString()}    delta="being investigated"               deltaTone="neutral"                            hint="under active triage"            icon={Clock3}       tone="gold"     testId="inc-kpi-triage" />
        <KpiTile label="High-severity"    value={counts.high.toString()}      delta={counts.high > 0 ? "Senior review" : "All clear"} deltaTone={counts.high > 0 ? "down" : "up"} hint="open + triage with sev high" icon={ShieldAlert}   tone="risk"     testId="inc-kpi-high" />
        <KpiTile label="Closed this cycle" value={counts.closedWk.toString()} delta="successfully resolved"             deltaTone="up"                                 hint="audit-chain closure recorded"   icon={CircleCheck}  tone="tralenex" testId="inc-kpi-closed" />
      </div>

      <StatusTabs current={status} setCurrent={setStatus} items={items} />

      <div className="mt-4 surface-card shadow-e1 overflow-hidden" data-testid="incident-panel">
        <Toolbar
          search={search} setSearch={setSearch}
          severity={severity} setSeverity={setSeverity}
          category={category} setCategory={setCategory}
          sortId={sortId} setSortId={setSortId}
          matchCount={filtered.length} totalCount={items.length}
          onReset={resetFilters}
        />
        <Table items={filtered} onSelect={setSelected} />
      </div>

      <IncidentDrawer
        incident={selected}
        onClose={() => setSelected(null)}
        onOpenAgent={(id) => navigate(`/govern/agents/${id}`)}
        onOpenPolicy={() => navigate("/govern/policies")}
        onClosure={closeIncident}
        writeAllowed={writeAllowed}
        readOnlyText={readOnlyReason(user?.id)}
      />
    </div>
  );
}

function Header({ lastSyncedAt, syncing, onRefetch }) {
  return (
    <div className="relative flex items-start justify-between gap-4 flex-wrap">
      <div>
        <p className="eyebrow">Govern · Incident Register</p>
        <h1 className="text-display-md text-ink-primary mt-1">Incidents</h1>
        <p className="text-ink-secondary mt-1 max-w-2xl">
          Every AI incident — from drift threshold breaches to vendor outages
          — recorded with a narrative timeline, owner, the policy that
          triggered it, and a one-click closure that lands straight in the
          audit chain.
        </p>
      </div>
      <SyncedIndicator
        lastSyncedAt={lastSyncedAt}
        loading={syncing}
        onRefetch={onRefetch}
        testId="incident-register-synced"
      />
    </div>
  );
}

function IncidentSkeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="incident-register-loading">
      <div className="h-8 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[420px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function IncidentError({ message }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="incident-register-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Govern · Incident Register</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load incidents</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
      </div>
    </div>
  );
}

function StatusTabs({ current, setCurrent, items }) {
  const counts = useMemo(() => {
    const c = { all: items.length, open: 0, triage: 0, closed: 0 };
    items.forEach((i) => { if (i.status in c) c[i.status] += 1; });
    return c;
  }, [items]);
  return (
    <div className="mt-6 inline-flex items-center gap-1 border border-border-subtle rounded-md p-1 bg-inset/30" data-testid="incident-status-tabs">
      {STATUS_TABS.map((t) => {
        const active = current === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => setCurrent(t.id)}
            data-testid={`incident-tab-${t.id}`}
            className={cn(
              "px-3 h-8 rounded text-sm font-medium tabular-nums transition-colors",
              active ? "bg-white text-ink-primary shadow-e1" : "text-ink-secondary hover:text-ink-primary",
            )}
          >
            {t.label}
            <span className="ml-1.5 text-meta text-ink-disabled">{counts[t.id]}</span>
          </button>
        );
      })}
    </div>
  );
}

function Toolbar({ search, setSearch, severity, setSeverity, category, setCategory, sortId, setSortId, matchCount, totalCount, onReset }) {
  return (
    <div className="px-4 py-3 border-b border-border-subtle flex flex-wrap items-center gap-2">
      <div className="relative flex-1 min-w-[240px]">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-disabled" />
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by ID, title, agent, owner…"
          data-testid="incident-search"
          className="w-full pl-8 pr-3 h-9 rounded-md border border-border-subtle bg-white text-sm placeholder:text-ink-disabled focus:outline-none focus:border-tralenex-500"
        />
      </div>
      <select value={severity} onChange={(e) => setSeverity(e.target.value)} data-testid="incident-severity-select" className="h-9 rounded-md border border-border-subtle bg-white text-sm px-2">
        {SEV_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
      <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="incident-category-select" className="h-9 rounded-md border border-border-subtle bg-white text-sm px-2">
        {CATEGORY_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
      <select value={sortId} onChange={(e) => setSortId(e.target.value)} data-testid="incident-sort-select" className="h-9 rounded-md border border-border-subtle bg-white text-sm px-2">
        {SORTS.map((s) => (<option key={s.id} value={s.id}>{s.label}</option>))}
      </select>
      <button type="button" onClick={onReset} data-testid="incident-reset" className="inline-flex items-center gap-1 h-9 px-2.5 rounded-md border border-border-subtle text-sm text-ink-secondary hover:text-ink-primary">
        <RefreshCw size={12} /> Reset
      </button>
      <span className="ml-auto text-meta text-ink-secondary tabular-nums" data-testid="incident-count">
        {matchCount} of {totalCount}
      </span>
    </div>
  );
}

function Table({ items, onSelect }) {
  if (!items.length) {
    return (
      <div className="py-16 text-center text-ink-secondary" data-testid="incident-empty">
        No incidents match the current filters.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="incident-table">
        <thead className="bg-inset/40 text-ink-secondary text-meta uppercase tracking-[0.06em]">
          <tr>
            {["ID", "Title", "Severity", "Status", "Owner", "Age", "Reported", ""].map((h) => (
              <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <Row key={i.id} item={i} onSelect={onSelect} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Row({ item, onSelect }) {
  const sevDotClass = sevDot(item.severity);
  const statusToneClass = statusTone(item.status);
  return (
    <tr className="border-t border-border-subtle hover:bg-inset/30 cursor-pointer" data-testid={`incident-row-${item.id}`} onClick={() => onSelect(item)}>
      <td className="px-3 py-2 font-mono text-meta text-ink-secondary whitespace-nowrap">{item.id}</td>
      <td className="px-3 py-2 text-ink-primary min-w-[280px]">
        <p className="leading-snug">{item.title}</p>
        {item.agentName && <p className="text-meta text-ink-secondary mt-0.5">{item.agentName}</p>}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className="inline-flex items-center gap-1.5 text-meta uppercase tracking-[0.06em] font-bold text-ink-secondary">
          <span className={cn("w-2 h-2 rounded-full", sevDotClass)} />
          {item.severity}
        </span>
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold tracking-[0.06em] uppercase", statusToneClass)}>
          {item.status}
        </span>
      </td>
      <td className="px-3 py-2 text-ink-primary whitespace-nowrap text-sm">
        {item.owner}
        {item.ownerRole && <span className="text-meta text-ink-secondary ml-1">· {item.ownerRole}</span>}
      </td>
      <td className="px-3 py-2 whitespace-nowrap tabular-nums text-ink-primary">{item.status === "closed" ? "—" : `${item.days || 0}d`}</td>
      <td className="px-3 py-2 text-meta text-ink-secondary whitespace-nowrap">{item.reported}</td>
      <td className="px-3 py-2 text-meta text-ink-disabled whitespace-nowrap"><ExternalLink size={12} /></td>
    </tr>
  );
}

function IncidentDrawer({ incident, onClose, onOpenAgent, onOpenPolicy, onClosure, writeAllowed, readOnlyText }) {
  if (!incident) return null;
  const sevDotClass = sevDot(incident.severity);
  const canClose = writeAllowed && incident.status !== "closed";
  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} data-testid="incident-drawer-backdrop" />
      <aside className="fixed top-0 right-0 h-full w-full max-w-[560px] bg-white border-l border-border-subtle z-50 overflow-y-auto shadow-2xl" data-testid="incident-drawer">
        <header className="px-6 py-5 border-b border-border-subtle sticky top-0 bg-white z-10">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-meta font-mono text-ink-secondary">{incident.id}</p>
              <h2 className="mt-1 text-h2 text-ink-primary leading-snug" data-testid="incident-drawer-title">{incident.title}</h2>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-meta text-ink-secondary">
                <span className="inline-flex items-center gap-1.5">
                  <span className={cn("w-2 h-2 rounded-full", sevDotClass)} />
                  <span className="uppercase tracking-[0.06em] font-bold">{incident.severity}</span>
                </span>
                <span>·</span>
                <span className="uppercase tracking-[0.06em] font-semibold">{incident.status}</span>
                <span>·</span>
                <span>{incident.category}</span>
                <span>·</span>
                <span>{incident.owner} ({incident.ownerRole})</span>
              </div>
            </div>
            <button type="button" onClick={onClose} className="text-ink-secondary hover:text-ink-primary p-1" data-testid="incident-drawer-close">
              <X size={18} />
            </button>
          </div>
        </header>
        <div className="px-6 py-5 space-y-6">
          <Section label="Impact">{incident.impact}</Section>
          <Section label="Narrative">{incident.narrative}</Section>
          <Section label="Cross-links">
            <div className="flex flex-wrap gap-2 text-sm">
              {incident.agentId && (
                <button type="button" onClick={() => onOpenAgent(incident.agentId)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border-subtle text-ink-primary hover:bg-inset/40" data-testid="incident-link-agent">
                  Agent · <span className="font-mono">{incident.agentId}</span>
                </button>
              )}
              {incident.policyId && (
                <button type="button" onClick={onOpenPolicy} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border-subtle text-ink-primary hover:bg-inset/40" data-testid="incident-link-policy">
                  Policy · <span className="font-mono">{incident.policyId}</span>
                </button>
              )}
              {!incident.agentId && !incident.policyId && <span className="text-ink-secondary text-meta">No direct cross-links.</span>}
            </div>
          </Section>
          <Section label="Timeline">
            <ol className="relative border-l border-border-subtle pl-5 space-y-3" data-testid="incident-timeline">
              {(incident.timeline || []).map((t) => (
                <li key={`${t.ts}|${t.actor}|${t.event}`} className="">
                  <span className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full bg-tralenex-500" />
                  <p className="text-meta font-mono text-ink-secondary">{t.ts}</p>
                  <p className="text-sm text-ink-primary leading-snug">{t.event}</p>
                  <p className="text-meta text-ink-secondary">{t.actor}</p>
                </li>
              ))}
              {(!incident.timeline || incident.timeline.length === 0) && <li className="text-ink-secondary text-meta">No timeline entries yet.</li>}
            </ol>
          </Section>
          {incident.closedAt && (
            <Section label="Closed">
              <p className="text-meta font-mono text-ink-secondary">{incident.closedAt}</p>
            </Section>
          )}
        </div>
        <footer className="px-6 py-4 border-t border-border-subtle sticky bottom-0 bg-white">
          <div className="flex justify-end gap-2" title={writeAllowed ? undefined : readOnlyText}>
            <button type="button" onClick={onClose} className="h-9 px-3 rounded-md border border-border-subtle text-sm text-ink-secondary hover:text-ink-primary">Close</button>
            <button
              type="button"
              disabled={!canClose}
              onClick={() => onClosure(incident)}
              data-testid="incident-drawer-resolve"
              className={cn(
                "h-9 px-3 rounded-md text-sm font-semibold inline-flex items-center gap-1",
                canClose ? "bg-tralenex-600 text-white hover:bg-tralenex-700" : "bg-inset text-ink-disabled cursor-not-allowed",
              )}
            >
              <CircleCheck size={14} /> {incident.status === "closed" ? "Already closed" : "Mark resolved"}
            </button>
          </div>
        </footer>
      </aside>
    </>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <p className="eyebrow">{label}</p>
      <div className="mt-1 text-sm text-ink-primary leading-snug">{children}</div>
    </div>
  );
}

function CustomerEmpty({ userName, onSetup }) {
  return (
    <div className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden anim-fade-up" data-testid="incident-customer-empty">
      <div className="absolute -right-40 -bottom-40 pointer-events-none">
        <Flywheel size={620} opacity={0.04} />
      </div>
      <div className="relative max-w-3xl">
        <p className="eyebrow">Govern · Incident Register</p>
        <h1 className="text-display-lg text-ink-primary mt-1">
          {userName ? `Welcome, ${userName}.` : "Welcome."}
        </h1>
        <p className="text-ink-secondary mt-3 max-w-xl text-[15px]">
          No incidents yet. When ALGOS detects a policy violation, drift
          breach or vendor outage, an incident lands here with a timeline,
          owner and one-click closure.
        </p>
        <button type="button" onClick={onSetup} data-testid="incident-empty-cta" className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700">
          Set up agent inventory
        </button>
      </div>
    </div>
  );
}
